import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import {
  cancelAppointment,
  getMyAppointments,
} from "../../api/clientPortalBooking";
import AuthRequiredCard from "../../components/clientPortal/AuthRequiredCard";
import { useClientPortalAuth } from "../../context/ClientPortalAuthContext";
import { parseLocalDateTimeInTimeZone } from "../../utils/datetime";
import { formatApiError } from "../../utils/formatApiError";

const formatLocalDateTime = (value) => {
  if (!value) return "";
  const parts = value.split("T");
  if (parts.length < 2) return value;
  return `${parts[0]} ${parts[1].slice(0, 5)}`;
};

export default function PortalMyAppointments() {
  const { salonId, settings } = useOutletContext();
  const { token } = useClientPortalAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [range, setRange] = useState("upcoming");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [appointments, setAppointments] = useState([]);
  const [tzName, setTzName] = useState("");
  const [cancelingId, setCancelingId] = useState(null);

  const cutoffHours = useMemo(() => {
    const raw = settings?.client_cancel_modify_cutoff_hours;
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return 24;
    return Math.min(168, Math.max(0, Math.round(parsed)));
  }, [settings?.client_cancel_modify_cutoff_hours]);

  useEffect(() => {
    if (!token) return;
    let active = true;
    const loadAppointments = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await getMyAppointments(range, token);
        if (!active) return;
        setAppointments(Array.isArray(data?.appointments) ? data.appointments : []);
        setTzName(data?.tz_name || "");
      } catch (err) {
        if (!active) return;
        setError(formatApiError(err));
      } finally {
        if (active) setLoading(false);
      }
    };
    loadAppointments();
    return () => {
      active = false;
    };
  }, [range, token]);

  useEffect(() => {
    const message = location?.state?.successMessage;
    if (message) {
      setSuccessMessage(message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const isWithinCutoff = (appt) => {
    if (!appt?.start_time_local || !tzName) return false;
    if (!cutoffHours || cutoffHours <= 0) return false;
    const startUtc = parseLocalDateTimeInTimeZone(appt.start_time_local, tzName);
    if (!startUtc) return false;
    const hoursUntil = (startUtc.getTime() - Date.now()) / 3600000;
    return hoursUntil < cutoffHours;
  };

  const handleCancel = async (apptId) => {
    if (!token || !apptId) return;
    setCancelingId(apptId);
    setError("");
    try {
      await cancelAppointment(apptId, token);
      const data = await getMyAppointments(range, token);
      setAppointments(Array.isArray(data?.appointments) ? data.appointments : []);
      setTzName(data?.tz_name || "");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setCancelingId(null);
    }
  };

  const showLoginPrompt = !token;

  if (showLoginPrompt) {
    return (
      <AuthRequiredCard
        title="Sign in to view your appointments"
        subtitle="Please log in to manage your upcoming visits."
        salonId={salonId}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl md:text-2xl font-semibold text-slate-900">
              My appointments
            </h2>
            <p className="mt-1 text-sm md:text-base text-slate-600">
              Review upcoming bookings or past visits.
            </p>
          </div>
          {tzName && (
            <span className="rounded-full border border-slate-200 px-4 py-2 text-xs md:text-sm font-semibold text-slate-600">
              Times shown in {tzName}
            </span>
          )}
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          {[
            { key: "upcoming", label: "Upcoming" },
            { key: "past", label: "Past" },
          ].map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setRange(tab.key)}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                range === tab.key
                  ? "bg-slate-900 text-white"
                  : "border border-slate-300 text-slate-700 hover:border-slate-400"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        {successMessage && (
          <div className="mb-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            {successMessage}
          </div>
        )}
        {error && (
          <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-sm text-slate-500">Loading appointments...</div>
        ) : (
          <div className="space-y-4">
            {appointments.map((appt) => {
              const canCancel =
                range === "upcoming" &&
                !["cancelled", "completed", "closed", "complete"].includes(
                  (appt.status || "").toLowerCase()
                );
              const withinCutoff = isWithinCutoff(appt);
              const canModify = canCancel && !withinCutoff;
              return (
                <div
                  key={appt.id}
                  className="rounded-2xl border border-slate-200 p-4"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="text-base font-semibold text-slate-900">
                        {appt.service_name || "Service"}
                      </div>
                      <div className="text-sm text-slate-600">
                        {formatLocalDateTime(appt.start_time_local)}
                        {appt.end_time_local
                          ? ` - ${formatLocalDateTime(appt.end_time_local).split(" ")[1]}`
                          : ""}
                      </div>
                      <div className="text-xs text-slate-500">
                        {appt.staff_name || "Technician"} · Status: {appt.status}
                      </div>
                      {withinCutoff && range === "upcoming" && (
                        <div className="mt-2 text-xs text-slate-500">
                          Changes disabled within {cutoffHours} hours of start time.
                        </div>
                      )}
                    </div>
                    {canCancel && (
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            navigate(
                              `/portal/salon/${salonId}/appointments/${appt.id}/reschedule`,
                              { state: { appointment: appt } }
                            )
                          }
                          disabled={!canModify}
                          className="rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                        >
                          Reschedule
                        </button>
                        <button
                          type="button"
                          onClick={() => handleCancel(appt.id)}
                          disabled={cancelingId === appt.id || !canModify}
                          className="rounded-full border border-rose-300 px-4 py-2 text-xs font-semibold text-rose-600 hover:bg-rose-50 disabled:opacity-60"
                        >
                          {cancelingId === appt.id ? "Cancelling..." : "Cancel"}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {!appointments.length && !loading && (
              <div className="text-sm text-slate-500">
                No appointments found.
              </div>
            )}
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-sm text-slate-600">
        Need help? Please contact the salon for assistance.
      </div>
    </div>
  );
}
